import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, SpaceBetween, Button, Input, ExpandableSection, Multiselect, Grid, Container, SegmentedControl, StatusIndicator, ProgressBar, Modal, Select, Spinner } from '@amzn/awsui-components-react';
import { AgGridReact } from 'ag-grid-react';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import craLogo from '../../images/cra_logo.png';
import NewSectionModal from './NewSectionModal';
import './styles.scss';
import { getDocumentTreeWithTracks, addDocumentTreeBlock, getDocumentFromStore, getDocumentBlocks, listAllServicesForApp, updateDocumentTreeConfig, deleteDocumentTreeBlock, updateDocumentTreeTrack, getServiceReferences, getServiceReferencesArray } from './DocumentStoreAPIs';
import { useSuggestedLogins } from 'src/hooks/useSuggestedLogins';
import { getLocalCache, setLocalCache } from 'src/utilities/CacheHelper';
import { v4 as uuidv4 } from 'uuid';
import { createAuditLog } from '../Templates/Utils/AppActivityUtils';
import { useSelector } from 'react-redux';
import ViewSupportModal from './ViewSupportModal';
import { listServicesWithTracksForQuarter, createPreference, listPreference, updatePreference } from 'src/utilities/AppSyncPlusAPIs';
import { getStatus, getOneWBStatusBasedOnCustomAttributes, getStatusForMultipleStatus, getWBStatusBasedOnCustomAttributes } from './Utility';
import ErrorTooltipPortal from './ErrorTooltipPortal';

const DEFAULT_STATUS_OPTIONS = [
    { label: 'Not started', value: 'Not started' },
    { label: 'Pending Support', value: 'Pending Support' },
    { label: 'Support Received', value: 'Support Received' },
    { label: 'Ready to Print', value: 'Ready to Print' },
    { label: 'Tie Out In Progress', value: 'Tie Out In Progress' },
    { label: 'Tied Out', value: 'Tied Out' },
    { label: 'Reviewed', value: 'Reviewed' },
    { label: 'Need to Reprint', value: 'Need to Reprint' },
    { label: 'Need to Fix Tie Out', value: 'Need to Fix Tie Out' },
    { label: 'No Tie Out Needed', value: 'No Tie Out Needed' },
    { label: 'Section review created', value: 'Section review created' },
    { label: 'Section Review Approved', value: 'Section Review Approved' },
    { label: 'In Progress', value: 'In Progress' },
];

const OVERALL_STATUS_OPTIONS = [
    { label: 'COMPLETED', value: 'COMPLETED' },
    { label: 'INCOMPLETE', value: 'INCOMPLETE' },
    { label: 'PENDING', value: 'PENDING' },
];

const OVERALL_STATUS_TYPE_MAP = {
    'COMPLETED': 'success',
    'INCOMPLETE': 'error',
    'PENDING': 'pending',
};


const SectionManagementComponent = ({
    sections: externalSections = [], loading: externalLoading = false, onRefresh,
    quarter, associationId, associationType, isAdmin = false,
    getStatusOptions = () => DEFAULT_STATUS_OPTIONS,
    getPartOptions = () => [],
    getPreparerOptions = () => [],
    getReviewerOptions = () => [],
    getDocumentOptions = () => [],
    getTieoutOptions = () => [{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }],
    getDeliverableOptions = () => [],
    onSaveSection,
}) => {
    const gridRef = useRef();
    const history = useHistory();
    const location = useLocation();
    const userId = useSelector((globalState) => globalState.auth.userId);
    const [quickFilterText, setQuickFilterText] = useState('');
    const [showFilters, setShowFilters] = useState(true);
    const [versionFilter, setVersionFilter] = useState('pre-wb');
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedParts, setSelectedParts] = useState([]);
    const [selectedPreparers, setSelectedPreparers] = useState([]);
    const [selectedReviewers, setSelectedReviewers] = useState([]);
    const [showNewSectionModal, setShowNewSectionModal] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [sections, setSections] = useState(externalSections);
    const [loading, setLoading] = useState(externalLoading);
    const [documentOptions, setDocumentOptions] = useState([]);
    const [selectedDocument, setSelectedDocument] = useState(() => {
        const params = new URLSearchParams(location.search);
        return params.get('document') || '10Q';
    });
    const [allBlocks, setAllBlocks] = useState([]);
    const [deliverableOptions, setDeliverableOptions] = useState([]);
    const [deliverablesLoading, setDeliverablesLoading] = useState(false);
    const [editStatusModal, setEditStatusModal] = useState(null);
    const [editStatusLoading, setEditStatusLoading] = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [supportSelectedRows, setSupportSelectedRows] = useState([]);
    const [supportDeliverables, setSupportDeliverables] = useState([]);
    const [tracksData, setTracksData] = useState([]);
    const [hasViewChanged, setHasViewChanged] = useState(false);
    const [gridViewPreference, setGridViewPreference] = useState({});
    const [preferenceButtonLoaderMessage, setPreferenceButtonLoaderMessage] = useState(false);
    const [editingCell, setEditingCell] = useState(null); // { rowId, field, savingValue, error }
    const [savingInlineEdit, setSavingInlineEdit] = useState(false);
    const inlineEditInputRef = useRef(null);
    const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Sync filters to/from URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const version = params.get('version');
        const parts = params.get('parts');
        const preparers = params.get('preparers');
        const reviewers = params.get('reviewers');
        const document = params.get('document');
        if (version) setVersionFilter(version);
        if (document) setSelectedDocument(document);
        if (parts) setSelectedParts(parts.split(',').map(v => ({ label: v, value: v })));
        if (preparers) setSelectedPreparers(preparers.split(',').map(v => ({ label: v, value: v })));
        if (reviewers) setSelectedReviewers(reviewers.split(',').map(v => ({ label: v, value: v })));
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        params.set('version', versionFilter);
        params.set('document', selectedDocument);
        if (selectedParts.length > 0) params.set('parts', selectedParts.map(p => p.value).join(','));
        else params.delete('parts');
        if (selectedPreparers.length > 0) params.set('preparers', selectedPreparers.map(p => p.value).join(','));
        else params.delete('preparers');
        if (selectedReviewers.length > 0) params.set('reviewers', selectedReviewers.map(r => r.value).join(','));
        else params.delete('reviewers');
        history.replace({ search: params.toString() });
    }, [versionFilter, selectedParts, selectedPreparers, selectedReviewers, selectedDocument]);

    const getCustomAttributeValue = (customAttributes, value) => customAttributes.find(customAttribute => customAttribute.attribute_type === value)?.attribute
    const getDueDateValue = (customAttributes, value) => {
        const dateAttr = getCustomAttributeValue(customAttributes, value);
        if (isNaN(parseInt(dateAttr))) return null;
        return new Date(parseInt(dateAttr))
    }
    const getDeliverableDueDate = (customAttributes) => {        
        return {
            "post": getCustomAttributeValue(customAttributes, "NO_POST_WB_DUE_DATE") === "true" ? 
                null : getDueDateValue(customAttributes, "POST_WB_DUE_DATE"),
            "pre": getCustomAttributeValue(customAttributes, "NO_PRE_WB_DUE_DATE") === "true" ? 
                null : getDueDateValue(customAttributes, "PRE_WB_DUE_DATE"),
        }
    }

    const fetchDeliverables = async (skipCache = false) => {
        setDeliverablesLoading(true);
        const cacheKey = `deliverables_${associationId}`;
        if (skipCache) localStorage.removeItem(cacheKey);
        let services = getLocalCache(cacheKey, 5 * 60 * 1000);
        if (!services) {
            services = await listAllServicesForApp(associationId);
            setLocalCache(cacheKey, services);
        }

        const options = [];
        (services?.frisco || []).forEach(f => {
            const dueDates = getDeliverableDueDate(f.custom_attributes);
            const tag = f.environment === 'Transfer_FAST_RedShift' ? 'to Workiva' : 'to Workdocs';
            const refs = getServiceReferencesArray(f);
            const refStr = getServiceReferences(f);
            options.push({ label: f.label, value: String(f.mapping_id), description: refStr || tag, tags: [tag], filteringTags: refs, deliverableType: 'FRISCO_MAPPING_ID', dueDates });
        });
        (services?.flexico || []).forEach(f => {
            const refs = getServiceReferencesArray(f);
            const refStr = getServiceReferences(f);
            options.push({ label: f.label, value: String(f.mapping_id), description: refStr || 'Automated', tags: ['Automated'], filteringTags: refs, deliverableType: 'FLEXICO_MAPPING_ID', dueDates: getDeliverableDueDate(f.custom_attributes) });
        });
        setDeliverableOptions(options);
        setDeliverablesLoading(false);
    };

    const fetchTracksData = () => {
        listServicesWithTracksForQuarter(associationId, null, quarter, true,
            (result) => {
                const data = JSON.parse(result.data.listServicesWithTracksForQuarter);
                setTracksData(data || []);
            },
            () => setTracksData([])
        );
    };

    const savePreference = async () => {
        if (!gridRef.current) return;
        const gridApi = gridRef.current.api;
        setPreferenceButtonLoaderMessage(true);
        const colState = gridApi.getColumnState();
        if (gridViewPreference?.preferenceId) {
            updatePreference(gridViewPreference.preferenceId, 'PREFERENCE', userId, colState, () => {
                setPreferenceButtonLoaderMessage(false);
                setHasViewChanged(false);
            });
        } else {
            const preferenceId = uuidv4();
            createPreference(preferenceId, 'SECTION_MGMT_PREFERENCE', 'PREFERENCE', associationId, 'APP', userId, colState, null, () => {
                setGridViewPreference({ preferenceId });
                setPreferenceButtonLoaderMessage(false);
                setHasViewChanged(false);
            });
        }
    };

    const resetView = () => {
        gridRef.current?.api?.resetColumnState();
        setHasViewChanged(true);
    };

    const loadSavedPreference = () => {
        if (!associationId || !gridRef.current?.api) return;
        listPreference(associationId, 'APP', 'SECTION_MGMT_PREFERENCE', (response) => {
            const item = response?.data?.listPreference?.items?.[0] || {};
            setGridViewPreference(item);
            if (item.information) {
                const info = typeof item.information === 'string' ? JSON.parse(item.information) : item.information;
                gridRef.current?.api?.applyColumnState({ state: info, applyOrder: true, defaultState: { flex: null } });
            }
        }, () => {});
    };

    // Load preferences after grid is ready and data loaded
    useEffect(() => {
        if (associationId && sections.length > 0) {
            loadSavedPreference();
        }
    }, [associationId, sections]);

    const handleGridViewChanges = (event) => {
        if (event.source === 'uiColumnMoved' || event.source === 'uiColumnResized' || event.source === 'uiColumnDragged' || event.source === 'columnMenu' || event.source === 'toolPanelUi') {
            setHasViewChanged(true);
        }
    };

    useEffect(() => {
        const fetchDocumentsAndBlocks = async () => {
            try {
                const docs = await getDocumentFromStore({ associationId, associationType });
                setDocumentOptions((docs || []).map(d => ({ label: d.document_name, value: String(d.document_id) })));

                const docIds = (docs || []).map(d => d.document_id);
                if (docIds.length > 0) {
                    const blocks = await getDocumentBlocks({ documentIds: docIds, blockType: 'PART' });
                    setAllBlocks(blocks || []);
                }

                await fetchDeliverables();
            } catch (e) {
                console.error('Failed to fetch documents/blocks', e);
            }
        };
        if (associationId && associationType) {
            fetchDocumentsAndBlocks();
        }
    }, [associationId, associationType]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await getDocumentTreeWithTracks({ quarter, associationId, associationType, documentName: selectedDocument });
                const rows = (Array.isArray(data) ? data : []).map(row => {
                    const attrs = typeof row.custom_attributes === 'string'
                        ? JSON.parse(row.custom_attributes)
                        : (row.custom_attributes || {});
                    return {
                        ...row,
                        page: attrs.PAGE || '',
                        pre_wb_due_date: '',
                        post_wb_due_date: '',
                        tieout: attrs.TIE_OUT_YN || '',
                        part_name: row.part_name || '',
                        preparer: (typeof row.preparers === 'string' ? JSON.parse(row.preparers) : (row.preparers || [])).map(p => p.alias).join(', '),
                        reviewer: (typeof row.reviewers === 'string' ? JSON.parse(row.reviewers) : (row.reviewers || [])).map(r => r.alias).join(', '),
                        service_mappings: typeof row.service_mappings === 'string' ? JSON.parse(row.service_mappings) : (row.service_mappings || []),
                    };
                });
                setSections(enrichSectionsWithDueDates(rows));
            } catch (e) {
                console.error('Failed to fetch document tree', e);
            } finally {
                setLoading(false);
            }
        };
        if (associationId && associationType) {
            fetchData();
        }
    }, [quarter, associationId, associationType, selectedDocument]);

    const getLatestDueDate = (dates) => {
        const validDates = dates
            .filter(d => d && d instanceof Date)
            .sort((a, b) => b - a);
        return validDates.length > 0 ? validDates[0] : null;
    };

    const getSectionDueDates = (serviceMappings) => {
        if (!serviceMappings || !Array.isArray(serviceMappings) || serviceMappings.length === 0 || deliverableOptions.length === 0) {
            return { pre_wb_due_date: '', post_wb_due_date: '' };
        }

        const mappingIds = serviceMappings.map(m => String(m.association_id));
        const matchedDeliverables = deliverableOptions.filter(opt => mappingIds.includes(String(opt.value)));

        const preDates = matchedDeliverables
            .map(d => d.dueDates?.pre)
            .filter(d => d != null);
        const postDates = matchedDeliverables
            .map(d => d.dueDates?.post)
            .filter(d => d != null);

        const latestPre = getLatestDueDate(preDates);
        const latestPost = getLatestDueDate(postDates);

        const formatDate = (d) => {
            if (!d) return '';
            return d instanceof Date ? d.toLocaleDateString() : '';
        };

        return {
            pre_wb_due_date: formatDate(latestPre),
            post_wb_due_date: formatDate(latestPost),
        };
    };

    const enrichSectionsWithDueDates = (localSections) => {
        if (deliverableOptions.length > 0 && localSections.length > 0) {
            return localSections.map(section => {
                const dueDates = getSectionDueDates(section.service_mappings);
                return { ...section, ...dueDates };
            });
        }
        return localSections;
    };

    useEffect(() => {
        if (deliverableOptions.length > 0) {
            setSections(prev => enrichSectionsWithDueDates(prev));
        }
    }, [deliverableOptions])

    const handleSaveSection = async (form) => {
        try {
            setLoading(true);
            const action = editingSection ? 'UPDATE_SECTION' : 'ADD_SECTION';
            createAuditLog(`DOCUMENT_STORE#${associationId}`, `${Date.now()}`, `${action} via section management`, JSON.stringify(editingSection || {}), JSON.stringify(form), action, userId);
            const selectedDocs = form.document ? [form.document] : [];
            const isNewPart = form.part && !allBlocks.some(b => b.block_name === form.part);

            // If part is new, create it for each selected document
            if (isNewPart) {
                for (const doc of selectedDocs) {
                    await addDocumentTreeBlock({
                        block_name: form.part,
                        block_type: 'PART',
                        block_description: 'adding from ABC',
                        document_id: doc.value,
                    });
                }
            }

            if (editingSection) {
                // Update existing section - use existing mapping_id so backend updates in place
                await updateDocumentTreeConfig({
                    group_type: 'SECTION',
                    association_id: editingSection.association_id,
                    association_type: 'BLOCK',
                    mapping_id: editingSection.mapping_id,
                    documentId: selectedDocs[0]?.value,
                    title: form.sectionName,
                    page: form.page,
                    part: (() => {
                        const existingBlock = allBlocks.find(b => b.block_name === form.part);
                        if (existingBlock) return { blockId: existingBlock.block_id };
                        return { partName: form.part, documentId: selectedDocs[0]?.value };
                    })(),
                    tieout: form.tieout?.value,
                    preparers: form.uploaders,
                    reviewers: form.approvers,
                    deliverables: (form.deliverables || []).map(d => ({
                        id: d.value,
                        type: d.deliverableType,
                    })),
                });
            } else {
                // Create new section block and get block_id
                for (const doc of selectedDocs) {
                    const result = await addDocumentTreeBlock({
                        block_name: form.sectionName,
                        block_type: 'SECTION',
                        block_description: 'adding from ABC',
                        document_id: doc.value,
                    });

                    if (result?.block_id) {
                        await updateDocumentTreeConfig({
                            group_type: 'SECTION',
                            association_id: result.block_id,
                            association_type: 'BLOCK',
                            title: form.sectionName,
                            page: form.page,
                            part: (() => {
                                const existingBlock = allBlocks.find(b => b.block_name === form.part);
                                if (existingBlock) return { blockId: existingBlock.block_id };
                                return { partName: form.part, documentId: doc.value };
                            })(),
                            tieout: form.tieout?.value,
                            preparers: form.uploaders,
                            reviewers: form.approvers,
                            deliverables: (form.deliverables || []).map(d => ({
                                id: d.value,
                                type: d.deliverableType,
                            })),
                        });
                    }
                }
            }

            onSaveSection?.(form);
        } catch (e) {
            console.error('Failed to add section', e);
        } finally {
            await refreshSections();
        }
    };

    const refreshSections = async () => {
        try {
            setLoading(true);
            await fetchDeliverables(true);
            fetchTracksData();
            const data = await getDocumentTreeWithTracks({ quarter, associationId, associationType, documentName: selectedDocument });
            const rows = (Array.isArray(data) ? data : []).map(row => {
                const attrs = typeof row.custom_attributes === 'string'
                    ? JSON.parse(row.custom_attributes)
                    : (row.custom_attributes || {});
                return {
                    ...row,
                    page: attrs.PAGE || '',
                    pre_wb_due_date: '',
                    post_wb_due_date: '',
                    tieout: attrs.TIE_OUT_YN || '',
                    part_name: row.part_name || '',
                    preparer: (typeof row.preparers === 'string' ? JSON.parse(row.preparers) : (row.preparers || [])).map(p => p.alias).join(', '),
                    reviewer: (typeof row.reviewers === 'string' ? JSON.parse(row.reviewers) : (row.reviewers || [])).map(r => r.alias).join(', '),
                    service_mappings: typeof row.service_mappings === 'string' ? JSON.parse(row.service_mappings) : (row.service_mappings || []),
                };
            });

            setSections(enrichSectionsWithDueDates(rows));
        } catch (e) {
            console.error('Failed to refresh sections', e);
        } finally {
            setLoading(false);
        }
    };

    const handleEditStatus = (data) => {
        const statusField = versionFilter === 'pre-wb' ? 'pre_wb_status' : 'post_wb_status';
        const overallStatusField = versionFilter === 'pre-wb' ? 'pre_wb_overall_status' : 'post_wb_overall_status';
        const currentStatus = data[statusField] || '';
        const currentOverallStatus = data[overallStatusField] || 'INCOMPLETE';
        setEditStatusModal({
            data,
            statusField,
            overallStatusField,
            newStatus: currentStatus,
            newOverallStatus: currentOverallStatus,
            title: data.title || 'Section',
        });
    };

    const handleSaveStatus = async () => {
        if (!editStatusModal) return;
        setEditStatusLoading(true);
        try {
            const { data, statusField, overallStatusField, newStatus, newOverallStatus } = editStatusModal;
            const wbType = versionFilter === 'pre-wb' ? 'PreWB' : 'PostWB';

            // Call the updateDocumentTreeTrack API to persist the status change
            await updateDocumentTreeTrack({
                action: 'UPDATE_DOCUMENT_TREE_TRACK',
                association_id: data.mapping_id,
                association_type: associationType,
                quarter,
                wb_type: wbType,
                current_status: newStatus,
                overall_status: newOverallStatus,
                updated_by: userId,
            });

            // Update the section's status locally
            setSections(prev => prev.map(section => {
                if (section === data || (section.mapping_id && section.mapping_id === data.mapping_id)) {
                    return { ...section, [statusField]: newStatus, [overallStatusField]: newOverallStatus };
                }
                return section;
            }));
            createAuditLog(
                `DOCUMENT_STORE#${associationId}`,
                `${Date.now()}`,
                `EDIT_STATUS via section management`,
                JSON.stringify({ [statusField]: data[statusField], [overallStatusField]: data[overallStatusField] }),
                JSON.stringify({ [statusField]: newStatus, [overallStatusField]: newOverallStatus }),
                'EDIT_STATUS',
                userId
            );
            setEditStatusModal(null);
        } catch (e) {
            console.error('Failed to update status', e);
        } finally {
            setEditStatusLoading(false);
        }
    };

    const handleViewSupport = () => {
        const selectedRows = gridRef.current?.api?.getSelectedRows() || [];
        if (selectedRows.length === 0) return;
        setSupportSelectedRows(selectedRows);
        setShowSupportModal(true);
    };

    const handleInlineCellDoubleClick = (rowData, field) => {
        if (savingInlineEdit) return;
        setEditingCell({ rowId: rowData.mapping_id, field });
    };

    const handleInlineCellKeyDown = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            await saveInlineEdit();
        } else if (e.key === 'Escape') {
            setEditingCell(null);
        }
    };

    const saveInlineEdit = async () => {
        if (!editingCell) return;
        const { rowId, field } = editingCell;
        const value = inlineEditInputRef.current?.value || '';
        const row = sections.find(s => s.mapping_id === rowId);
        if (!row) { setEditingCell(null); return; }

        // Convert comma-separated aliases to the array format expected by the API
        // Backend deletes ALL aliases then re-inserts, so we must always send both preparers and reviewers
        // Preparers use type 'UPLOADER' (column_index 0), Reviewers use type 'APPROVER' (column_index 1)
        const aliases = value.split(',').map(a => a.trim()).filter(Boolean);

        if (aliases.length > 0) {
            setEditingCell(prev => prev ? { ...prev, error: null, savingValue: value } : null);
            setSavingInlineEdit(true);

            try {
                // Validate each alias using getSuggestedLoginsCaller from useSuggestedLogins hook
                // which filters by ALLOWED_COST_CENTERS and returns {label, value} objects
                const validationResults = await Promise.all(
                    aliases.map(async (alias) => {
                        const suggestions = await getSuggestedLoginsCaller(alias, 10);
                        // Check if any returned suggestion is an exact match for the alias
                        const exactMatch = (suggestions || []).some(
                            s => s.value === alias.toLowerCase().trim()
                        );
                        return { alias, isValid: exactMatch };
                    })
                );

                const invalidAliases = validationResults
                    .filter(result => !result.isValid)
                    .map(result => result.alias);

                if (invalidAliases.length > 0) {
                    setEditingCell(prev => prev ? { ...prev, error: `Invalid alias(es): ${invalidAliases.join(', ')}`, savingValue: undefined, currentValue: value } : null);
                    setSavingInlineEdit(false);
                    return;
                }
            } catch (e) {
                console.error('Failed to validate aliases', e);
                setEditingCell(prev => prev ? { ...prev, error: 'Failed to validate aliases. Please try again.', savingValue: undefined, currentValue: value } : null);
                setSavingInlineEdit(false);
                return;
            }
        } else {
            setEditingCell(prev => prev ? { ...prev, savingValue: value, error: null } : null);
            setSavingInlineEdit(true);
        }

        // Build the current preparers/reviewers from the row data to preserve the other field
        const currentPreparers = row.preparer
            ? row.preparer.split(',').map(a => a.trim()).filter(Boolean).map(alias => ({ alias, type: 'UPLOADER' }))
            : [];
        const currentReviewers = row.reviewer
            ? row.reviewer.split(',').map(a => a.trim()).filter(Boolean).map(alias => ({ alias, type: 'APPROVER' }))
            : [];

        const updatePayload = {
            group_type: 'SECTION',
            association_id: row.association_id,
            association_type: 'BLOCK',
            mapping_id: row.mapping_id,
            preparers: field === 'preparer'
                ? aliases.map(alias => ({ alias, type: 'UPLOADER' }))
                : currentPreparers,
            reviewers: field === 'reviewer'
                ? aliases.map(alias => ({ alias, type: 'APPROVER' }))
                : currentReviewers,
        };
        
        try {
            await updateDocumentTreeConfig(updatePayload);
            // Update local state - update both the display string and the raw array
            // so the edit form (NewSectionModal) also reflects the new data
            setSections(prev => prev.map(section => {
                if (section.mapping_id === rowId) {
                    const updatedSection = { ...section, [field]: aliases.join(', ') };
                    if (field === 'preparer') {
                        updatedSection.preparers = aliases.map(alias => ({ alias, type: 'UPLOADER' }));
                    } else if (field === 'reviewer') {
                        updatedSection.reviewers = aliases.map(alias => ({ alias, type: 'APPROVER' }));
                    }
                    return updatedSection;
                }
                return section;
            }));
            createAuditLog(
                `DOCUMENT_STORE#${associationId}`,
                `${Date.now()}`,
                `INLINE_EDIT_${field.toUpperCase()} via section management`,
                JSON.stringify({ [field]: row[field] }),
                JSON.stringify({ [field]: aliases.join(', ') }),
                `INLINE_EDIT_${field.toUpperCase()}`,
                userId
            );
        } catch (e) {
            console.error(`Failed to update ${field}`, e);
        } finally {
            setSavingInlineEdit(false);
            setEditingCell(null);
        }
    };

    const inlineEditableCellRenderer = (field) => (params) => {
        const isEditing = editingCell && editingCell.rowId === params.data.mapping_id && editingCell.field === field;
        if (isEditing) {
            if (savingInlineEdit) {
                return (
                    <div className="inline-edit-wrapper">
                        <input
                            className="inline-edit-input"
                            value={editingCell.savingValue || ''}
                            disabled
                            readOnly
                        />
                        <span className="inline-edit-loading-spinner">
                            <Spinner />
                        </span>
                    </div>
                );
            }
            return (
                <div className="inline-edit-wrapper">
                    <input
                        autoFocus
                        ref={inlineEditInputRef}
                        className={`inline-edit-input${editingCell.error ? ' inline-edit-input-error' : ''}`}
                        defaultValue={editingCell.currentValue !== undefined ? editingCell.currentValue : (params.data[field] || '')}
                        onKeyDown={handleInlineCellKeyDown}
                    />
                    <button
                        className="inline-edit-btn inline-edit-btn-save"
                        onClick={saveInlineEdit}
                        title="Save"
                        type="button"
                    >
                        ✓
                    </button>
                    <button
                        className="inline-edit-btn inline-edit-btn-cancel"
                        onClick={() => setEditingCell(null)}
                        title="Cancel"
                        type="button"
                    >
                        ✕
                    </button>
                    {editingCell.error && (
                        <ErrorTooltipPortal inputRef={inlineEditInputRef} message={editingCell.error} />
                    )}
                </div>
            );
        }
        return (
            <span
                onDoubleClick={() => handleInlineCellDoubleClick(params.data, field)}
                style={{ cursor: 'pointer', display: 'block', width: '100%', minHeight: '20px' }}
                title="Double-click to edit"
            >
                {params.value || ''}
            </span>
        );
    };

    // Fetch tracks data for status display
    useEffect(() => {
        if (associationId && quarter) {
            fetchTracksData();
        }
    }, [associationId, quarter]);

    const enrichedDeliverableOptions = React.useMemo(() => {
        if (!deliverableOptions.length || !tracksData.length) return deliverableOptions;
        return deliverableOptions.map(d => {
            const track = tracksData.find(t => t.mapping_id && String(t.mapping_id) === d.value);
            let preWbReceived = false;
            let postWbReceived = false;
            if (track) {
                const preWbStatus = getStatusForMultipleStatus(track, ['PreWB']);
                const postWbStatus = getStatusForMultipleStatus(track, ['PostWB', 'NoPostWB']);
                preWbReceived = preWbStatus?.overall_status === 'COMPLETED';
                postWbReceived = postWbStatus?.overall_status === 'COMPLETED';
            }
            return { ...d, preWbReceived, postWbReceived };
        });
    }, [deliverableOptions, tracksData]);

    const enrichedSections = React.useMemo(() => {
        if (!sections.length || !enrichedDeliverableOptions.length) return sections;
        if (enrichedDeliverableOptions[0]?.preWbReceived === undefined) return sections;
        return sections.map(row => {
            const mappings = row.service_mappings || [];
            if (!mappings.length) return row;
            const enrichedMappings = mappings.map(sm => {
                const match = enrichedDeliverableOptions.find(o =>
                    String(sm.association_id) === o.value && sm.association_type === o.deliverableType
                );
                if (!match) return { ...sm, label: '', preWbReceived: false, postWbReceived: false };
                return { ...sm, label: match.description, preWbReceived: match.preWbReceived, postWbReceived: match.postWbReceived };
            });
            return { ...row, service_mappings: enrichedMappings };
        });
    }, [sections, enrichedDeliverableOptions]);

    const maxDeliverables = React.useMemo(() => {
        if (!enrichedSections.length) return 0;
        let max = 0;
        enrichedSections.forEach(row => {
            const count = (row.service_mappings || []).length;
            if (count > max) max = count;
        });
        return max;
    }, [enrichedSections]);

    const deliverableColumns = React.useMemo(() => {
        const cols = [];
        for (let i = 0; i < maxDeliverables; i++) {
            const idx = i;
            cols.push({
                headerName: `Deliverable ${idx + 1}`,
                colId: `deliverable_${idx}`,
                width: 250,
                sortable: false,
                filter: false,
                cellRenderer: (params) => {
                    const sm = params.data?.service_mappings?.[idx];
                    if (!sm) return null;
                    const isReceived = versionFilter === 'pre-wb' ? sm.preWbReceived : sm.postWbReceived;
                    const statusLabel = isReceived ? 'Received' : 'Outstanding';
                    const statusType = isReceived ? 'success' : 'pending';
                    return (
                        <SpaceBetween direction="vertical" size="xxxs">
                            <Box >{sm.label}</Box>
                            <Box><StatusIndicator type={statusType}>{statusLabel}</StatusIndicator></Box>
                        </SpaceBetween>
                    );
                },
            });
        }
        return cols;
    }, [maxDeliverables, versionFilter]);

    // Helper: Check if all deliverables for a section row are "Received" based on current version filter
    const areAllDeliverablesReceived = (rowData) => {
        const mappings = rowData?.service_mappings || [];
        if (mappings.length === 0) return false; // No deliverables means status cannot be updated
        return mappings.every(sm => {
            return versionFilter === 'pre-wb' ? sm.preWbReceived : sm.postWbReceived;
        });
    };

    const defaultColDef = { filter: true, sortable: true, resizable: true, filterParams: { buttons: ['reset'], debounceMs: 200 } };
    const statusCellRenderer = (params) => {
        const status = params.value;
        if (!status) return null;
        const overallStatusVersion = versionFilter === 'pre-wb' ? 'pre_wb_overall_status' : 'post_wb_overall_status';
        const overallStatus = params.data[overallStatusVersion]
        const type = OVERALL_STATUS_TYPE_MAP[overallStatus] || 'pending';
        const allReceived = areAllDeliverablesReceived(params.data);
        const isStatusLocked = !allReceived;

        if (isStatusLocked) {
            return (
                <SpaceBetween direction="horizontal" size="xxs">
                    <span className="status-cell-locked" title="Deliverables not yet received">
                        <StatusIndicator type="pending">{status}</StatusIndicator>
                    </span>
                </SpaceBetween>
            );
        }

        return (
            <SpaceBetween direction="horizontal" size="xxs">
                {isAdmin && (<Button iconName="edit" variant="icon" onClick={() => handleEditStatus(params.data)} />)}
                <span className={type === 'error' ? 'status-icon-hidden' : ''}>
                    <StatusIndicator type={type}>{status}</StatusIndicator>
                </span>
            </SpaceBetween>
        );
    };

    const columnDefs = [
        {
            headerName: '', width: 50, sortable: false, filter: false, resizable: false,
            checkboxSelection: true, headerCheckboxSelection: true,
        },
        {
            headerName: '', width: 70, sortable: false, filter: false, resizable: false,
            cellRenderer: (params) => isAdmin ? <Button iconName="settings" variant="inline-icon" onClick={() => { setEditingSection(params.data); setShowNewSectionModal(true); }} /> : null,
        },
        { field: 'page', headerName: 'Page', width: 140, sort: 'asc', comparator: (a, b) => {
            const numA = parseInt(a, 10);
            const numB = parseInt(b, 10);
            if (isNaN(numA) && isNaN(numB)) return (a || '').localeCompare(b || '');
            if (isNaN(numA)) return 1;
            if (isNaN(numB)) return -1;
            return numA - numB;
        }},
        { field: 'part_name', headerName: 'Part', width: 150 },
        { field: 'title', headerName: 'Description', width: 250 },
        { field: versionFilter === 'pre-wb' ? 'pre_wb_status' : 'post_wb_status', headerName: versionFilter === 'pre-wb' ? 'Pre-WB status' : 'Post-WB status', width: 250, cellRenderer: statusCellRenderer },
        { field: versionFilter === 'pre-wb' ? 'pre_wb_due_date' : 'post_wb_due_date', headerName: 'Due Date', width: 150 },
        { field: 'preparer', headerName: 'Preparer', width: 160, cellRenderer: inlineEditableCellRenderer('preparer'), filterValueGetter: (params) => params.data?.preparer?.split(', ') || [] },
        { field: 'reviewer', headerName: 'Reviewer', width: 160, cellRenderer: inlineEditableCellRenderer('reviewer'), filterValueGetter: (params) => params.data?.reviewer?.split(', ') || [] },
        { field: 'last_updated', headerName: 'Last updated', width: 200 },
        { field: versionFilter === 'pre-wb' ? 'pre_wb_progress' : 'post_wb_progress', headerName: 'Deliverable Collection Progress', width: 220, cellRenderer: (params) => {
            const val = params.value || 0;
            return <div className={val >= 100 ? 'progress-bar-success-wrapper' : ''}><ProgressBar value={val} /></div>;
        }},
        ...deliverableColumns,
    ];

    const onFilterTextChange = useCallback(({ detail }) => {
        setQuickFilterText(detail.value);
        gridRef.current?.api?.setGridOption('quickFilterText', detail.value);
    }, []);

    const clearAllFilters = () => {
        setSelectedStatuses([]); setSelectedParts([]); setSelectedPreparers([]); setSelectedReviewers([]);
        gridRef.current?.api?.setFilterModel(null);
    };

    useEffect(() => {
        gridRef.current?.api?.onFilterChanged();
    }, [selectedStatuses, selectedParts, selectedPreparers, selectedReviewers]);

    const isExternalFilterPresent = useCallback(() => {
        return selectedStatuses.length > 0 || selectedParts.length > 0 || selectedPreparers.length > 0 || selectedReviewers.length > 0;
    }, [selectedStatuses, selectedParts, selectedPreparers, selectedReviewers]);

    const doesExternalFilterPass = useCallback((node) => {
        const data = node.data;
        if (selectedStatuses.length > 0) {
            const statusField = versionFilter === 'pre-wb' ? data.pre_wb_status : data.post_wb_status;
            if (!selectedStatuses.some(s => s.value === statusField)) return false;
        }
        if (selectedParts.length > 0) {
            if (!selectedParts.some(p => p.value === data.part_name)) return false;
        }
        if (selectedPreparers.length > 0) {
            const preparerList = data.preparer ? data.preparer.split(', ') : [];
            if (!selectedPreparers.some(p => preparerList.includes(p.value))) return false;
        }
        if (selectedReviewers.length > 0) {
            const reviewerList = data.reviewer ? data.reviewer.split(', ') : [];
            if (!selectedReviewers.some(r => reviewerList.includes(r.value))) return false;
        }
        return true;
    }, [selectedStatuses, selectedParts, selectedPreparers, selectedReviewers, versionFilter]);

    const filterConfigs = [
        { key: 'status', label: 'Status', placeholder: 'Select status', options: getStatusOptions(), selected: selectedStatuses, onChange: setSelectedStatuses },
        { key: 'part', label: 'Part', placeholder: 'Select part', options: [...new Set(allBlocks.map(b => b.block_name).filter(Boolean))].map(name => ({ label: name, value: name })), selected: selectedParts, onChange: setSelectedParts },
        { key: 'preparer', label: 'Preparer', placeholder: 'Select preparer', options: [...new Set(sections.flatMap(s => s.preparer ? s.preparer.split(', ') : []))].map(a => ({ label: a, value: a })), selected: selectedPreparers, onChange: setSelectedPreparers },
        { key: 'reviewer', label: 'Reviewer', placeholder: 'Select reviewer', options: [...new Set(sections.flatMap(s => s.reviewer ? s.reviewer.split(', ') : []))].map(a => ({ label: a, value: a })), selected: selectedReviewers, onChange: setSelectedReviewers },
    ];

    return (
        <Grid gridDefinition={showFilters ? [{ colspan: 2 }, { colspan: 10 }] : [{ colspan: 12 }]}>
            {showFilters && (
                <Container>
                    <SpaceBetween size="l">
                        <Box><img src={craLogo} alt="SEC Central" className="cra-logo" /></Box>
                        {filterConfigs.map(({ key, label, placeholder, options, selected, onChange }) => (
                            <ExpandableSection key={key} headerText={label} defaultExpanded>
                                <Multiselect selectedOptions={selected} onChange={({ detail }) => onChange(detail.selectedOptions)} options={options} placeholder={placeholder} filteringType="auto" checkboxes keepOpen />
                            </ExpandableSection>
                        ))}
                        <Button variant="normal" onClick={clearAllFilters}>Clear filters</Button>
                    </SpaceBetween>
                </Container>
            )}
            <SpaceBetween size="m">
                <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                    <Grid gridDefinition={[{ colspan: 1 }, { colspan: 11 }]}>
                        <Button iconName={showFilters ? 'angle-left' : 'filter'} variant="icon" onClick={() => setShowFilters(!showFilters)} />
                        <Input value={quickFilterText} onChange={onFilterTextChange} placeholder="Search for..." type="search" />
                    </Grid>
                    <SpaceBetween direction="horizontal" size="s" alignItems="center">
                        {documentOptions.length > 1 && <SegmentedControl selectedId={selectedDocument} onChange={({ detail }) => setSelectedDocument(detail.selectedId)} options={documentOptions.map(d => ({ text: d.label, id: d.label }))} />}
                        <SegmentedControl selectedId={versionFilter} onChange={({ detail }) => setVersionFilter(detail.selectedId)} options={[{ text: 'Pre-WB', id: 'pre-wb' }, { text: 'Post-WB', id: 'post-wb' }]} />
                        <Button variant="normal" onClick={handleViewSupport} disabled={supportSelectedRows.length === 0}>View Support</Button>
                    </SpaceBetween>
                </Grid>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <SpaceBetween direction="horizontal" size="s" alignItems="center">
                        {isAdmin && <Button iconName="add-plus" variant="normal" onClick={() => setShowNewSectionModal(true)} />}
                        <Button iconName="refresh" variant="icon" onClick={refreshSections} />
                        <Box variant="h3">Sections ({sections.length})</Box>
                    </SpaceBetween>
                    <SpaceBetween direction="horizontal" size="s" alignItems="center">
                        {preferenceButtonLoaderMessage && <Box><Spinner /> Saving...</Box>}
                        {hasViewChanged && !preferenceButtonLoaderMessage && <Button variant="primary" onClick={savePreference}>Save view</Button>}
                        {!preferenceButtonLoaderMessage && <Button variant="inline-link" onClick={resetView}>Reset view</Button>}
                    </SpaceBetween>
                </div>
                <Box>
                    <div className="ag-theme-alpine" style={{ width: '100%' }}>
                        <AgGridReact ref={gridRef} rowData={enrichedSections} columnDefs={columnDefs} defaultColDef={defaultColDef} domLayout="autoHeight" loading={loading} animateRows={true} enableCellTextSelection={true} suppressCellFocus={true} isExternalFilterPresent={isExternalFilterPresent} doesExternalFilterPass={doesExternalFilterPass} rowSelection="multiple" suppressRowClickSelection={true} onSelectionChanged={() => setSupportSelectedRows(gridRef.current?.api?.getSelectedRows() || [])} sideBar={{toolPanels:[{id:'columns',labelDefault:'Columns',labelKey:'columns',iconKey:'columns',toolPanel:'agColumnsToolPanel'}],defaultToolPanel:''}} onColumnVisible={handleGridViewChanges} onColumnMoved={handleGridViewChanges} onColumnResized={handleGridViewChanges} onColumnPinned={handleGridViewChanges} onSortChanged={handleGridViewChanges} />
                    </div>
                </Box>
            </SpaceBetween>
            {showNewSectionModal && <NewSectionModal
                visible={showNewSectionModal}
                onDismiss={() => { setShowNewSectionModal(false); setEditingSection(null); }}
                onSave={handleSaveSection}
                onDelete={async (data, comment) => {
                    try {
                        setLoading(true);
                        createAuditLog(`DOCUMENT_STORE#${associationId}`, `${Date.now()}`, comment || 'DELETE_SECTION via section management', JSON.stringify(data), JSON.stringify({}), 'DELETE_SECTION', userId);
                        await deleteDocumentTreeBlock({ mapping_id: data.mapping_id, block_id: data.association_id });
                        await refreshSections();
                    } catch (e) {
                        console.error('Failed to delete section', e);
                        setLoading(false);
                    }
                }}
                editData={editingSection}
                getPartOptions={() => partOptions}
                getDocumentOptions={() => documentOptions}
                allBlocks={allBlocks}
                getPreparerOptions={getPreparerOptions}
                getReviewerOptions={getReviewerOptions}
                getTieoutOptions={getTieoutOptions}
                getDeliverableOptions={() => deliverableOptions}
                deliverablesLoading={deliverablesLoading}
            />}
            {editStatusModal && (
                <Modal
                    onDismiss={() => setEditStatusModal(null)}
                    visible={!!editStatusModal}
                    header={`Edit Status - ${editStatusModal.title}`}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setEditStatusModal(null)}>Cancel</Button>
                                <Button variant="primary" onClick={handleSaveStatus} loading={editStatusLoading}>Save</Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween direction="vertical" size="l">
                        <SpaceBetween direction="horizontal" size="m">
                            <Box variant="awsui-key-label">Current status:</Box>
                            <span className={OVERALL_STATUS_TYPE_MAP[editStatusModal.data[editStatusModal.overallStatusField]] === 'error' ? 'status-icon-hidden' : ''}>
                                <StatusIndicator type={OVERALL_STATUS_TYPE_MAP[editStatusModal.data[editStatusModal.overallStatusField]] || 'pending'}>
                                    {editStatusModal.data[editStatusModal.statusField] || 'None'}
                                </StatusIndicator>
                            </span>
                        </SpaceBetween>
                        <SpaceBetween direction="horizontal" size="m">
                            <Box variant="awsui-key-label">Overall status:</Box>
                            <Select
                                selectedOption={editStatusModal.newOverallStatus ? { label: editStatusModal.newOverallStatus, value: editStatusModal.newOverallStatus } : null}
                                onChange={({ detail }) => {
                                    setEditStatusModal(prev => ({ ...prev, newOverallStatus: detail.selectedOption.value }));
                                }}
                                options={OVERALL_STATUS_OPTIONS}
                                placeholder="Select overall status"
                            />
                        </SpaceBetween>
                        <SpaceBetween direction="horizontal" size="m">
                            <Box variant="awsui-key-label">New status:</Box>
                            <Select
                                selectedOption={editStatusModal.newStatus ? { label: editStatusModal.newStatus, value: editStatusModal.newStatus } : null}
                                onChange={({ detail }) => {
                                    setEditStatusModal(prev => ({ ...prev, newStatus: detail.selectedOption.value }));
                                }}
                                options={DEFAULT_STATUS_OPTIONS}
                                placeholder="Select new status"
                            />
                        </SpaceBetween>
                    </SpaceBetween>
                </Modal>
            )}
            <ViewSupportModal
                visible={showSupportModal}
                onDismiss={() => { setShowSupportModal(false); }}
                selectedSections={supportSelectedRows}
                deliverableOptions={deliverableOptions}
                tracksData={tracksData}
                wbType={versionFilter === 'pre-wb' ? 'Pre-WB' : 'Post-WB'}
                loading={deliverablesLoading}
            />
        </Grid>
    );
};

export default SectionManagementComponent;
